import { FormattedMessage } from 'react-intl';

import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { cardAttributes, glossary } from '@sorare/core/src/lib/glossary';

import { widget as ShowDetails } from '#src/components/TokenPreview/ShowDetails/index.tsx';
import {
  BackendDuplicateFilter,
  DuplicateFilter,
} from '#src/searchCards/DuplicateFilter/index.tsx';
import { RefineBackendCardPlayerAge } from '#src/searchCards/RefineAge/index.tsx';
import { RefineBackendAppearances } from '#src/searchCards/RefineAppearances/index.tsx';
import { RefineBackendAverageScore } from '#src/searchCards/RefineAverageScore/index.tsx';
import {
  RefineActiveClub,
  RefineBackendActiveClub,
  RefineBackendClub,
  RefineClub,
} from '#src/searchCards/RefineClubs/index.tsx';
import {
  RefineBackendGameplayTier,
  RefineGameplayTier,
} from '#src/searchCards/RefineGameplayTier/index.tsx';
import {
  RefineBackendActiveLeague,
  RefineBackendActiveNationalTeam,
  RefineBackendCardEdition,
  RefineBackendNationality,
  RefineBackendPlayer,
  RefineBackendPlayingStatus,
  RefineBackendSeason,
} from '#src/searchCards/RefineList/index.tsx';
import {
  backendWidget as BackendRefineSkin,
  widget as RefineSkin,
} from '#src/searchCards/RefineSkin/index.tsx';
import {
  AcceptedCurrencyGroup,
  BackendGameweekFilter,
  BidToEarnFilter,
  CollectibleFilter,
  CollectibleGroup,
  GameweekFilter,
  NotInLineupFilter,
  PriceGroup,
  PromotionalEventFilter,
  PromotionalEventFilterForInstantBuy,
  RarityGroup,
  RefineActiveLeague,
  RefineActiveNationalTeam,
  RefineAppearances,
  RefineAverageScore,
  RefineBackendPosition,
  RefineCardEdition,
  RefineCardLevel,
  RefineCardPlayerAge,
  RefineCompetitionEligibility,
  RefineCreditEligible,
  RefineInSeason,
  RefineNationality,
  RefineOnSale,
  RefinePlayer,
  RefinePlayingStatus,
  RefinePosition,
  RefineSeason,
  RefineSerialNumber,
  RevealableCardCampaignMenu,
  SaleGroup,
  filterSeparator,
} from '#src/searchCards/index.ts';
import { WatchlistGroup } from '#src/searchCards/refineGroups.tsx';

import { widget as FootballCustomDecksFilter } from './FootballCustomDecksFilter/index.tsx';

const CardGroup = (props?: {
  withoutTeam?: boolean;
  withoutCompetitionEligibility: boolean;
}) => ({
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [
    ...((props?.withoutCompetitionEligibility && []) || [
      RefineCompetitionEligibility,
    ]),
    RefinePosition(),
    RefineSeason,
    ...((props?.withoutTeam && []) || [RefineClub()]),
  ].filter(Boolean),
});

export const MatchInfoGroup = {
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [GameweekFilter, RefineAverageScore(), RefineAppearances()],
};

export const PlayerGroup = (props?: {
  withoutActiveClub?: boolean;
  withoutNationalTeam?: boolean;
  withPositions?: boolean;
  withPlayingStatus?: boolean;
}) => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    ...(props?.withPositions ? [RefinePosition()] : []),
    ...(props?.withPlayingStatus ? [RefinePlayingStatus] : []),
    RefineCardPlayerAge,
    ...((props?.withoutNationalTeam && []) || [RefineNationality]),
    RefinePlayer,
    RefineActiveLeague(),
    ...((props?.withoutActiveClub && []) || [RefineActiveClub()]),
    ...((props?.withoutNationalTeam && []) || [RefineActiveNationalTeam]),
  ].filter(Boolean),
});

const ToggleGroup = (additionalFilters?: FilterWidget[]) => ({
  slug: 'toggle',
  title: null,
  filters: [
    ...(additionalFilters?.flatMap(filter => [filterSeparator, filter]) || []),
  ],
});
const InSeasonFilter = {
  slug: 'inSeason',
  title: null,
  filter: RefineInSeason,
  filters: [],
};

export const primaryMarketFilters = [
  {
    slug: 'PromotionalEvent',
    title: null,
    filters: [PromotionalEventFilter],
  },
  RarityGroup,
  PriceGroup,
  ToggleGroup([BidToEarnFilter()]),
  WatchlistGroup,
  CardGroup(),
  MatchInfoGroup,
  PlayerGroup(),
  CollectibleGroup([RefineSerialNumber, RefineCardEdition]),
  SaleGroup,
];

export const instantBuyMarketFilters = [
  {
    slug: 'PromotionalEvent',
    title: null,
    filters: [PromotionalEventFilterForInstantBuy],
  },
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup(),
  MatchInfoGroup,
  PlayerGroup(),
  CollectibleGroup([RefineSerialNumber, RefineCardEdition]),
];

export const secondaryMarketFilters = [
  InSeasonFilter,
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup(),
  MatchInfoGroup,
  PlayerGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

export const secondaryMarketStackedFilters = [
  InSeasonFilter,
  PriceGroup,
  CardGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
].filter(Boolean);

export const playerPageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  CardGroup({ withoutCompetitionEligibility: true }),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

export const tradeFilters = [
  InSeasonFilter,
  RarityGroup,
  { slug: 'not-in-lineup', filters: [NotInLineupFilter] },
  WatchlistGroup,
  {
    slug: 'deck',
    filters: [FootballCustomDecksFilter],
  },
  CardGroup({ withoutCompetitionEligibility: false, withoutTeam: false }),
];

export const clubPageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup({ withoutCompetitionEligibility: true, withoutTeam: true }),
  PlayerGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

export const leaguePageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup({ withoutCompetitionEligibility: true }),
  MatchInfoGroup,
  PlayerGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

export const countryPageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup(),
  MatchInfoGroup,
  PlayerGroup({ withoutNationalTeam: true }),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

export const myFootballCommonCardsFilters = ({
  filterBy,
  setSlug,
}: {
  filterBy: (item: any) => boolean;
  setSlug: Nullable<string>;
}) => [
  {
    slug: 'skin',
    title: null,
    filters: [RefineSkin(setSlug)],
  },
  {
    slug: 'duplicatedKey',
    filters: [DuplicateFilter],
  },

  {
    title: <FormattedMessage {...FILTERS.activeLeague.title} />,
    slug: 'eligibility',
    filters: [
      RefineActiveLeague({
        sortBy: ['count:desc'],
        hideTitle: true,
        showMore: false,
        searchable: false,
        filterBy,
      }),
    ],
  },
  {
    slug: 'position',
    title: <FormattedMessage {...FILTERS.position.title} />,
    filters: [
      RefinePosition({
        hideTitle: true,
      }),
    ],
  },
  {
    slug: 'gameplay_tier',
    title: <FormattedMessage {...glossary.tier} />,
    filters: [
      RefineGameplayTier({
        hideTitle: true,
      }),
    ],
  },
];

export const myClubFilters = ({
  showDecksFilter,
}: {
  showDecksFilter: boolean;
}) =>
  [
    { slug: 'show-details', filters: [ShowDetails] },

    InSeasonFilter,
    ToggleGroup([RefineOnSale(), NotInLineupFilter]),
    ...((showDecksFilter && [
      { slug: 'deck', filters: [FootballCustomDecksFilter] },
    ]) ||
      []),
    WatchlistGroup,
    CardGroup(),
    MatchInfoGroup,
    PlayerGroup(),
    CollectibleGroup([
      RefineSerialNumber,
      RefineCardLevel,
      RefineCardEdition,
      CollectibleFilter,
    ]),
    AcceptedCurrencyGroup,
  ].filter(Boolean);

export const myVaultFilters = [
  { slug: 'show-details', filters: [ShowDetails] },
  InSeasonFilter,
  CardGroup(),
  PlayerGroup(),
  CollectibleGroup([RefineSerialNumber, RefineCardEdition, CollectibleFilter]),
];

export const revealableCardsFilters = [
  { slug: 'revealable-card-campaign', filters: [RevealableCardCampaignMenu] },
  CardGroup(),
  PlayerGroup(),
  CollectibleGroup([RefineSerialNumber, CollectibleFilter]),
];

const BackendCardGroup = () => ({
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [
    RefineBackendPosition(),
    RefineBackendSeason,
    RefineBackendClub(),
  ].filter(Boolean),
});

const BackendMatchInfoGroup = {
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [
    BackendGameweekFilter,
    RefineBackendAverageScore(),
    RefineBackendAppearances(),
  ],
};

const BackendPlayerGroup = () => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    RefineBackendCardPlayerAge,
    RefineBackendNationality,
    RefineBackendPlayer,
    RefineBackendActiveLeague(),
    RefineBackendActiveClub(),
    RefineBackendActiveNationalTeam,
    RefineBackendPlayingStatus,
  ].filter(Boolean),
});

export const backendMyFootballCommonCardsFilters = ({
  filterBy,
  setSlug,
}: {
  filterBy: (item: any) => boolean;
  setSlug: Nullable<string>;
}) => [
  {
    slug: 'skin',
    title: null,
    filters: [BackendRefineSkin(setSlug)],
  },
  {
    slug: 'duplicatedKey',
    filters: [BackendDuplicateFilter],
  },
  {
    title: <FormattedMessage {...FILTERS.activeLeague.title} />,
    slug: 'eligibility',
    filters: [
      RefineBackendActiveLeague({
        showMore: false,
        searchable: false,
        filterBy,
      }),
    ],
  },
  {
    slug: 'position',
    title: <FormattedMessage {...FILTERS.position.title} />,
    filters: [
      RefineBackendPosition({
        hideTitle: true,
      }),
    ],
  },
  {
    slug: 'gameplay_tier',
    title: <FormattedMessage {...glossary.tier} />,
    filters: [
      RefineBackendGameplayTier({
        hideTitle: true,
      }),
    ],
  },
];

export const backendMyClubFilters = () =>
  [
    { slug: 'show-details', filters: [ShowDetails] },
    BackendCardGroup(),
    BackendMatchInfoGroup,
    BackendPlayerGroup(),
    {
      slug: 'collectible',
      title: <FormattedMessage {...cardAttributes.collectibles} />,
      filters: [RefineBackendCardEdition],
    },
  ].filter(Boolean);
